'use strict';

import VZAddBase from './VZAddBase';
import { getJourney } from 'Root/journey/journeyHelper';

export default class VZAddLocation extends VZAddBase {
    constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.addToCartUrl = this.dataset.addToCartUrl;
        this.removeFromCartUrl = this.dataset.removeFromCartUrl;
        this.csSolutionId = null;
    }

    connectedCallback() {
        this.querySelector('button').addEventListener('click', this.onSubmit);
    }

    disconnectedCallback() {
        this.querySelector('button').removeEventListener('click', this.onSubmit);
    }

    /**
     * @function getLinkedProducts
     * @return {Array} - An array of linked products or an empty Array.
     * @description Looks for the links products
     */
    static getLinkedProducts() {
        return [document.getElementById('linkedProducts'), document.getElementById('optionalBusinessRulesAddons')]
            .map((element) => JSON.parse(element.value))
            .flat()
            .filter((product) => typeof product === 'string' || (product.pid && product.quantity))
            .map((product) => {
                if (typeof product === 'string') {
                    return { pid: product, quantity: 1 };
                }
                return product;
            });
    }

    /**
     * @function getMainProductId
     * @return {string} - Main product ID
     * @description Returns the main product ID which the addons are linked to
     */
    static getMainProductId() {
        return document.getElementById('mainProductId').value || '';
    }

    /**
     * @function submitLinkedProducts
     * @return {Promise} - A resolved promise
     * @param {boolean} inJourney - Is in journey
     * @description Submits all the products which are linked to the configured one.
     */
    submitLinkedProducts(inJourney) {
        const products = this.constructor.getLinkedProducts().map((product) => {
            const formData = new FormData();
            formData.set('quantity', 1);
            formData.set('pid', product.pid);
            formData.set('paid', this.constructor.getPAID().value);
            formData.set('belongsToGuidedJourney', inJourney);

            // Add the upgraded tier for Multi Product Benefits
            const upgradedTier = document.getElementById('upgradedTier');
            const downgradedTier = document.getElementById('downgradedTier');
            formData.set('upgradedTier', upgradedTier ? upgradedTier.value : null);
            formData.set('downgradedTier', downgradedTier ? downgradedTier.value : null);

            if (product.pid === this.constructor.getMainProductId()) {
                formData.set('addons', this.constructor.getAddons());
            }
            ['postalCode', 'houseNumber', 'houseNumberAddition', 'room'].forEach((field) => {
                formData.append(field, document.getElementById(`js-active-address-${field}`)?.value || '');
            });
            // If the previous call returned csSolutionId, this call has to use the same solution
            if (this.csSolutionId !== null) {
                formData.set('csSolutionId', this.csSolutionId);
            }
            return formData;
        });
        const submitProductsAsync = async () => {
            /* eslint-disable no-await-in-loop */
            for (let i = 0; i < products.length; i++) {
                // If the previous call returned csSolutionId, this call has to use the same solution
                if (this.csSolutionId !== null) {
                    products[i].set('csSolutionId', this.csSolutionId);
                }
                await fetch(this.form.action, {
                    // eslint-disable-line
                    method: 'POST',
                    body: products[i]
                })
                    .then((response) => {
                        if (response.status === 500) throw new Error('Network error');
                        return response.json();
                    })
                    .then((response) => {
                        if (response && response.csSolutionId) this.csSolutionId = response.csSolutionId;
                    })
                    .catch(this.constructor.handleNetworkError);
            }
        };
        return submitProductsAsync();
    }

    /**
     * @function submitPresets
     * @description Submits all products within a package
     * @returns {Promise} a promise
     */
    submitPresets() {
        const presets = document.querySelector('input[name="presets"]')?.value;
        if (presets) {
            const parsedPresets = JSON.parse(presets);
            const promises = Object.keys(parsedPresets)
                .reduce((prev, current) => {
                    prev.push(parsedPresets[current].products);
                    return prev;
                }, [])
                .flat()
                .map(({ ajaxAttributes }) => {
                    const formData = new FormData();
                    if (this.csSolutionId !== null) {
                        formData.set('csSolutionId', this.csSolutionId);
                    }
                    Object.keys(ajaxAttributes).forEach((key) => {
                        if (key === 'paid' && ajaxAttributes[key] === null) {
                            ajaxAttributes[key] = this.constructor.getPAID()?.value;
                        }

                        if (typeof ajaxAttributes[key] === 'object') {
                            ajaxAttributes[key] = JSON.stringify(ajaxAttributes[key]);
                        }

                        formData.append(key, ajaxAttributes[key]);
                    });

                    return fetch(this.form.action, {
                        method: 'post',
                        body: formData
                    });
                });

            return Promise.all(promises);
        }
        return Promise.all([]);
    }

    /**
     * @function onSubmit
     * @param {SubmitEvent} event - The form submit
     * @description Perform different actions based on whether the user is in editing mode or not
     */
    async onSubmit(event) {
        event.preventDefault();
        this.classList.add('pe-none');

        // If we are in editing mode, remove the old location first
        if (this.editMode) {
            await fetch(document.querySelector('#remove-location').action, {
                method: 'POST'
            });
        }

        this.form.appendChild(this.constructor.getPAID());
        [...this.form.querySelectorAll('input[name="tier"]')].forEach((field) => field.setAttribute('disabled', 'disabled'));

        await this.submitPresets();
        this.submitForm.call(this);
    }

    /**
     * @function submitForm
     * @description Submits all linked products, followed by the configured product
     */
    async submitForm() {
        this.spinner.start();

        const isPartOfJourney =
            document.querySelector('#internet')?.checked ||
            document.querySelector('#business-mobile')?.checked ||
            document.querySelector('[name="isPartOfJourney"]')?.value === 'true';

        const inJourney = getJourney()?.length > 0 && isPartOfJourney && !!document.getElementById('journey-modal');

        await this.submitLinkedProducts(inJourney);

        // prepare configuratorType field
        const configuratorType = document.createElement('input');
        configuratorType.setAttribute('type', 'hidden');
        configuratorType.setAttribute('name', 'configuratorType');
        configuratorType.setAttribute('value', 'business-internet');
        this.form.appendChild(configuratorType);

        if (inJourney) {
            const belongsToGuidedJourney = document.createElement('input');
            belongsToGuidedJourney.setAttribute('type', 'hidden');
            belongsToGuidedJourney.setAttribute('name', 'belongsToGuidedJourney');
            belongsToGuidedJourney.setAttribute('value', inJourney);
            this.form.appendChild(belongsToGuidedJourney);
        }

        const formData = new FormData(this.form);
        ['city', 'street', 'room'].forEach((field) => {
            formData.append(field, document.querySelector(`.js-postcode-response input#${field}`)?.value || '');
        });
        ['postalCode', 'houseNumber', 'houseNumberAddition', 'room'].forEach((field) => {
            formData.append(field, document.getElementById(`js-active-address-${field}`)?.value || '');
        });
        if (this.csSolutionId !== null) {
            formData.set('csSolutionId', this.csSolutionId);
        }

        fetch(this.form.action, {
            method: 'post',
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) throw new Error('Error');

                // After having added the product to the cart, show the "added to basket" modal or journey modal
                const modal = document.getElementById(inJourney ? 'journey-modal' : 'add-to-basket-modal');
                const hasNonCsProductsSelected = !inJourney && Array.from(document.getElementsByClassName('js-non_cs_product') ?? '').some(({ checked }) => checked);
                const bodyText = document.getElementById('addToBasketBodyNonCPQText')?.value;
                if (!inJourney && response.hideAddToBasketModal) {
                    window.location.href = response.basketUrl;
                } else {
                    modal.render(hasNonCsProductsSelected ? { bodyText } : {});
                }

                /* eslint-disable no-underscore-dangle */
                if (response.ecommerceObject && window._ddm) {
                    window._ddm.trigger('ecommerce.add_to_cart', {
                        data: response.ecommerceObject.ecommerce.addToCart
                    });
                }
                /* eslint-enable no-underscore-dangle */
            })
            .catch(this.constructor.handleNetworkError)
            .finally(this.spinner.stop);
    }
}

if (!window.customElements.get('vz-add-location')) {
    window.customElements.define('vz-add-location', VZAddLocation);
}
